import './GraphSnapshotCreation.css';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { type NodeSn, isDeviceType } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { AnimationClose } from 'components/AnimationClose/AnimationClose';
import { PortalToContainer } from 'components/PortalToContainer/PortalToContainer';
import { SnapshotCreationNextStep } from 'components/SnapshotCreation/SnapshotCreationNextStep';
import { SnapshotCreationTable } from 'components/SnapshotCreation/SnapshotCreationTable';
import { useSnapshotCreation } from 'components/SnapshotCreation/useSnapshotCreation';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { flow } from 'fp-ts/lib/function';
import { SettingsPage } from 'pages/SnapshotManagement/SettingsPage';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { memo, useMemo } from 'react';
import type { FC } from 'react';
import { isOneOf } from 'utils/isOneOf';
import { useGraphContextSelectors } from '../GraphContext';
import type { GraphState } from '../GraphState';

export const getDeviceTypeNodeSns = (state: GraphState): NodeSn[] =>
  Object.values(state.graph.nodes)
    .filter(flow(prop('type'), isDeviceType))
    .map(prop('sn'));

interface Props {
  onClose: () => void;
}

const BareGraphSnapshotCreation: FC<Props> = (props) => {
  const { contentWrapper } = useGlobalStateContextSelectors({
    contentWrapper: prop('contentWrapper'),
  });
  const msg = useLocale();
  const {
    canvasGraph: { snapshotCreation: snapshotCreationMsg },
  } = msg;

  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    graphLoading: (s) => isOneOf<LoadingState>(s.loading, 'INITIAL', 'LOADING'),
    loadedGraphSnapshotId: (s) => s.graphMetadata?.snapshotId ?? null,
    nodeSns: getDeviceTypeNodeSns,
  });

  const snapshotCreation = useSnapshotCreation({
    defaultTabId: 'createSnapshot',
    onClose: props.onClose,
    preselectedSns: ctxProps.nodeSns,
    snapshotId: ctxProps.loadedGraphSnapshotId,
  });
  const snapshots = useSnapshots();

  const currentSnapshot = useMemo(
    () =>
      snapshots.find(
        (snapshot) => snapshot.id === ctxProps.loadedGraphSnapshotId,
      ),
    [snapshots, ctxProps.loadedGraphSnapshotId],
  );

  if (!currentSnapshot) {
    return null;
  }

  const {
    breadcrumbsStep,
    failedData,
    onBreadcrumbsStep1Click,
    onContinueDiscovery,
    onSnapshotSettingsClose,
    onSnapshotSettingsOpen,
    onSourceSelect,
    requestErrors,
    selectedSns,
    sendingDataState,
    snapshotSettingsTab,
    taskApiSourceMap,
  } = snapshotCreation;

  const handleSettingsSave = useStableCallback(() => {
    onContinueDiscovery({ notSelectedDeviceSns: [] });
  });

  return (
    <PortalToContainer container={contentWrapper}>
      <div className='match-parent z-popup flex flex-col bg-white'>
        {ctxProps.loadedGraphSnapshotId !== null && (
          <SettingsPage
            foundSnapshot={currentSnapshot}
            newSnapshot={false}
            onClose={onSnapshotSettingsClose}
            onSettingsSaved={handleSettingsSave}
            showSettings={snapshotSettingsTab !== null}
          />
        )}
        <div className='mx flex border-0 border-b border-solid border-gray-100'>
          <div className='flex-1'>
            <h2>{snapshotCreationMsg.title}</h2>
            <p>{snapshotCreationMsg.description}</p>
          </div>
          <div className='mt'>
            <AnimationClose className='float-right' onClick={props.onClose} />
          </div>
        </div>
        <div className='relative flex flex-1 flex-col overflow-auto p-4'>
          {ctxProps.graphLoading && <LoaderAndText />}
          {!ctxProps.graphLoading && breadcrumbsStep === 1 && (
            <SnapshotCreationTable
              canCreateNewSnapshot
              disableLinks
              preselectedSns={ctxProps.nodeSns}
              snapshotCreation={snapshotCreation}
            />
          )}
          {!ctxProps.graphLoading && requestErrors !== null && (
            <SnapshotCreationNextStep
              breadcrumbsStep={breadcrumbsStep}
              onBreadcrumbsStep1Click={onBreadcrumbsStep1Click}
              failedData={failedData}
              onContinueDiscovery={onContinueDiscovery}
              onSnapshotSettingsOpen={onSnapshotSettingsOpen}
              onSourceSelect={onSourceSelect}
              requestErrors={requestErrors}
              selectedSns={selectedSns}
              sendingDataState={sendingDataState}
              taskApiSourceMap={taskApiSourceMap}
            />
          )}
        </div>
      </div>
    </PortalToContainer>
  );
};

export const GraphSnapshotCreation = memo(BareGraphSnapshotCreation);
